import {Alert, Box, Button, Group, Modal, Stack, Text, Title, Tooltip, Anchor} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useState} from 'react';

import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {disableMfa} from 'auth/MfaDAO';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface ProfileMfaSectionProps {
  mfaEnabled: boolean;
  canDisableMfa: boolean;
}

export default function ProfileMfaSection(props: ProfileMfaSectionProps): JSX.Element {
  const [confirmOpened, {open: openConfirm, close: closeConfirm}] = useDisclosure(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(props.mfaEnabled);

  const handleDisable = (): void => {
    setIsDisabling(true);
    disableMfa()
      .then(() => {
        setMfaEnabled(false);
      })
      .catch((res) => {
        notifications.notifyError(res);
      })
      .finally(() => {
        closeConfirm();
        setIsDisabling(false);
      });
  };

  return (
    <Box {...getTestProps({component: 'ProfileMfaSection', element: 'root'})}>
      <Stack spacing="sm">
        <Title order={2}>Multi-factor authentication (MFA)</Title>
        <Group spacing="sm">
          {mfaEnabled ? (
            <>
              <Alert
                variant="success"
                icon={<BenchlingIcon icon="check-circle" />}
                title="Your account is secured with MFA"
                {...getTestProps({component: 'ProfileMfaSection', element: 'enabledAlert'})}
              >
                Use the app on your mobile device to sign in
              </Alert>
              <Tooltip
                label="Your admin requires MFA on all accounts. If your device has been compromised or lost, contact your admin to set up a new one."
                position="bottom"
                multiline={true}
                w={420}
                disabled={props.canDisableMfa}
              >
                <Box w="fit-content">
                  <Button
                    variant="warn"
                    size="sm"
                    w="fit-content"
                    px={20}
                    disabled={!props.canDisableMfa}
                    onClick={openConfirm}
                  >
                    Disable MFA
                  </Button>
                </Box>
              </Tooltip>
            </>
          ) : (
            <>
              <Text size="sm">
                Multi-factor authentication secures your account with a OTP (One Time Password) application.{' '}
                <Anchor
                  href="https://help.benchling.com/hc/en-us/articles/39934848089997-Configure-and-administer-tenant-and-data-access#h_01M4GD71HPNJ1YG19RQJPZ37NZ"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </Anchor>
              </Text>
              <Button
                component="a"
                href="/settings/mfa"
                variant="secondary"
                size="sm"
                w="fit-content"
                {...getTestProps({component: 'ProfileMfaSection', element: 'enableMfaButton'})}
              >
                Enable Multi-factor authentication
              </Button>
            </>
          )}
          <ExportAuditLogButton
            additionalOptions={{reportType: 'userMfa', modalTitleOverride: 'MFA Audit Log'}}
          />
        </Group>
      </Stack>
      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        title="Are you sure you want to disable MFA?"
        centered={true}
        size="sm"
        closeOnEscape={!isDisabling}
        withCloseButton={!isDisabling}
        closeOnClickOutside={!isDisabling}
      >
        <Stack spacing="md">
          <Text size="sm">
            <Text span={true} fw={600}>
              Multi-factor auth helps keep your account secure.
            </Text>{' '}
            If you’re sure, click “Disable MFA”. Note that if you re-enable MFA, you will have to scan a new
            QR code on your authenticator app.
          </Text>
          <Group position="right" spacing="sm">
            <Button variant="secondary" size="sm" onClick={closeConfirm} disabled={isDisabling}>
              Cancel
            </Button>
            <Button variant="warn" size="sm" loading={isDisabling} onClick={handleDisable}>
              Disable MFA
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
